import React, { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { API } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Download, Edit2, CheckCircle2, XCircle, RefreshCw, MapPin, ExternalLink } from "lucide-react";

/** Phase 2 — PM/Admin desktop Attendance Management. */
export default function AttendanceManagement() {
  const today = new Date().toISOString().slice(0, 10);
  const [stats, setStats] = useState(null);
  const [rows, setRows] = useState([]);
  const [leaves, setLeaves] = useState([]);
  const [loading, setLoading] = useState(true);
  const [date, setDate] = useState(today);
  const [month, setMonth] = useState(today.slice(0, 7));
  const [overrideRow, setOverrideRow] = useState(null);
  // Iter42 — per-promoter filter + status filter
  const [promoters, setPromoters] = useState([]);
  const [promoterFilter, setPromoterFilter] = useState("");
  const [dealerFilter, setDealerFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [personaFilter, setPersonaFilter] = useState("");
  const [history, setHistory] = useState(null);   // {promoter, rows[]}
  const [loadingHist, setLoadingHist] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [s, l, lv] = await Promise.all([
        api.get("/promoter/attendance/today-active"),
        api.get("/promoter/attendance/list", { params: { date } }),
        api.get("/promoter/attendance/leave-requests", { params: { status: "pending" } }),
      ]);
      setStats(s.data);
      setRows(l.data || []);
      setLeaves(lv.data || []);
    } catch (e) {
      toast.error("Could not load", { description: formatApiErrorDetail(e.response?.data?.detail) });
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, [date]);
  // Promoter list loaded once for the dropdown
  useEffect(() => {
    api.get("/promoter/promoters").then((r) => setPromoters(r.data || [])).catch(() => {});
  }, []);

  // Pull per-promoter monthly summary when filter is set
  const loadHistory = async (pid) => {
    if (!pid) { setHistory(null); return; }
    setLoadingHist(true);
    try {
      const { data } = await api.get("/promoter/attendance/promoter-summary", {
        params: { promoter_id: pid, month },
      });
      setHistory(data);
    } catch {
      setHistory(null);
    } finally { setLoadingHist(false); }
  };
  useEffect(() => { loadHistory(promoterFilter); /* eslint-disable-next-line */ }, [promoterFilter, month]);

  const rowMatchesStatus = (r) => {
    if (!statusFilter) return true;
    if (statusFilter === "present") return !r.absent && !r.check_in_late && !r.check_out_early;
    if (statusFilter === "absent")  return !!r.absent;
    if (statusFilter === "late")    return !!r.check_in_late;
    if (statusFilter === "early")   return !!r.check_out_early;
    if (statusFilter === "leave")   return r.on_leave === true || r.status === "leave";
    return true;
  };
  const filteredRows = rows
    .filter((r) => !promoterFilter || r.promoter_id === promoterFilter)
    .filter((r) => !dealerFilter || (r.check_in_dealer_name || "").toLowerCase().includes(dealerFilter.toLowerCase()))
    .filter((r) => !personaFilter || (r.promoter_role || r.role || "sales_promoter") === personaFilter)
    .filter(rowMatchesStatus);

  const decideLeave = async (lid, decision) => {
    try {
      await api.patch(`/promoter/attendance/leave-requests/${lid}/decide`, { decision });
      toast.success(`Leave ${decision}`);
      load();
    } catch (e) {
      toast.error("Could not decide", { description: formatApiErrorDetail(e.response?.data?.detail) });
    }
  };

  const exportXlsx = () => {
    const token = localStorage.getItem("access_token");
    fetch(`${API}/promoter/attendance/export.xlsx?month=${encodeURIComponent(month)}`, {
      headers: { Authorization: `Bearer ${token}` },
    }).then((r) => {
      if (!r.ok) throw new Error("export failed");
      return r.blob();
    }).then((blob) => {
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a"); a.href = url; a.download = `attendance_${month}.xlsx`; a.click();
      URL.revokeObjectURL(url);
    }).catch(() => toast.error("Could not export"));
  };

  return (
    <div className="space-y-4" data-testid="attendance-management">
      <header className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h1 className="font-heading text-3xl font-bold text-slate-900">Attendance</h1>
          <p className="text-sm text-slate-500 mt-1">Daily attendance, leave approvals, manual corrections, monthly export.</p>
        </div>
        <div className="flex gap-2 items-end flex-wrap">
          <div>
            <Label className="text-[11px] uppercase font-semibold text-slate-600">Date</Label>
            <Input type="date" value={date} onChange={(e) => setDate(e.target.value)}
                   className="h-8 w-40" data-testid="att-filter-date" />
          </div>
          <div>
            <Label className="text-[11px] uppercase font-semibold text-slate-600">Export month</Label>
            <Input type="month" value={month} onChange={(e) => setMonth(e.target.value)}
                   className="h-8 w-40" data-testid="att-export-month" />
          </div>
          <Button variant="outline" onClick={exportXlsx} data-testid="att-export-btn">
            <Download size={14} className="mr-1" /> Excel
          </Button>
          <Button variant="outline" onClick={load} data-testid="att-refresh-btn">
            <RefreshCw size={14} className="mr-1" /> Refresh
          </Button>
        </div>
      </header>

      {/* Iter42 — additional filters */}
      <Card><CardContent className="p-3 grid grid-cols-2 sm:grid-cols-5 gap-2 text-xs" data-testid="att-filters">
        <label className="space-y-1">
          <span className="font-semibold text-slate-600 uppercase tracking-wider text-[10px]">Promoter</span>
          <select value={promoterFilter} onChange={(e) => setPromoterFilter(e.target.value)}
                  className="border border-slate-200 rounded-md w-full h-8 px-1.5 bg-white"
                  data-testid="att-filter-promoter">
            <option value="">— All promoters —</option>
            {promoters.map((p) => <option key={p.id} value={p.id}>{p.name} ({p.promoter_id})</option>)}
          </select>
        </label>
        <label className="space-y-1">
          <span className="font-semibold text-slate-600 uppercase tracking-wider text-[10px]">Dealer</span>
          <Input value={dealerFilter} onChange={(e) => setDealerFilter(e.target.value)}
                 placeholder="dealer name…"
                 className="h-8" data-testid="att-filter-dealer" />
        </label>
        <label className="space-y-1">
          <span className="font-semibold text-slate-600 uppercase tracking-wider text-[10px]">Status</span>
          <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}
                  className="border border-slate-200 rounded-md w-full h-8 px-1.5 bg-white"
                  data-testid="att-filter-status">
            <option value="">— Any status —</option>
            <option value="present">Present</option>
            <option value="absent">Absent</option>
            <option value="late">Late</option>
            <option value="early">Early Checkout</option>
            <option value="leave">On Leave</option>
          </select>
        </label>
        <label className="space-y-1">
          <span className="font-semibold text-slate-600 uppercase tracking-wider text-[10px]">Persona</span>
          <select value={personaFilter} onChange={(e) => setPersonaFilter(e.target.value)}
                  className="border border-slate-200 rounded-md w-full h-8 px-1.5 bg-white"
                  data-testid="att-filter-persona">
            <option value="">— Any persona —</option>
            <option value="sales_promoter">Sales Promoter</option>
            <option value="self_promoter">Self Promoter</option>
          </select>
        </label>
        <div className="flex items-end">
          <Button variant="ghost" className="h-8 text-xs"
                  onClick={() => { setPromoterFilter(""); setDealerFilter(""); setStatusFilter(""); setPersonaFilter(""); }}
                  data-testid="att-filter-clear">Clear filters</Button>
        </div>
      </CardContent></Card>

      {/* Iter42 — individual promoter summary (when filter is set) */}
      {promoterFilter && (
        <Card><CardContent className="p-3" data-testid="att-promoter-summary">
          {loadingHist ? (
            <div className="text-sm text-slate-400">Loading promoter summary…</div>
          ) : history?.summary ? (
            <>
              <div className="font-semibold text-slate-900 text-sm mb-2">
                {history.promoter?.name || "Promoter"} — {month}
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-6 gap-2 text-xs">
                <SummaryStat label="Working Days" value={history.summary.working_days} />
                <SummaryStat label="Present" value={history.summary.present_days} tone="emerald" />
                <SummaryStat label="Absent" value={history.summary.absent_days} tone="rose" />
                <SummaryStat label="Leave" value={history.summary.leave_days} tone="indigo" />
                <SummaryStat label="Late" value={history.summary.late_days} tone="amber" />
                <SummaryStat label="Early Out" value={history.summary.early_checkout_days} tone="amber" />
              </div>
            </>
          ) : (
            <div className="text-xs text-slate-500">No history found for this promoter in {month}.</div>
          )}
        </CardContent></Card>
      )}

      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3" data-testid="att-stats">
          <Stat label="Active Promoters" value={stats.total_active_promoters} testid="stat-total" />
          <Stat label="Checked In Today" value={stats.checked_in} testid="stat-in" tone="emerald" />
          <Stat label="Still on Field" value={stats.still_on_field} testid="stat-field" tone="amber" />
          <Stat label="Late" value={stats.late} testid="stat-late" tone="rose" />
          <Stat label="Absent" value={stats.absent} testid="stat-absent" tone="slate" />
        </div>
      )}

      {/* Pending leaves */}
      <Card>
        <CardContent className="p-4 space-y-2">
          <div className="font-semibold text-slate-900">Pending leave requests ({leaves.length})</div>
          {leaves.length === 0 && (
            <div className="text-xs text-slate-500" data-testid="no-pending-leaves">No pending leave requests.</div>
          )}
          {leaves.map((lv) => (
            <div key={lv.id} className="border border-slate-100 rounded p-2 flex items-center gap-3 text-sm"
                 data-testid={`leave-pending-${lv.id.slice(0, 8)}`}>
              <div className="flex-1">
                <div className="font-medium">{lv.promoter_name}</div>
                <div className="text-xs text-slate-600 font-mono">{lv.from_date} → {lv.to_date}</div>
                <div className="text-xs text-slate-500">{lv.reason}</div>
              </div>
              <Button size="sm" className="bg-emerald-700 hover:bg-emerald-800 text-white"
                      onClick={() => decideLeave(lv.id, "approved")}
                      data-testid={`leave-approve-${lv.id.slice(0, 8)}`}>
                <CheckCircle2 size={12} className="mr-1" /> Approve
              </Button>
              <Button size="sm" variant="outline" className="text-rose-700 hover:bg-rose-50"
                      onClick={() => decideLeave(lv.id, "rejected")}
                      data-testid={`leave-reject-${lv.id.slice(0, 8)}`}>
                <XCircle size={12} className="mr-1" /> Reject
              </Button>
            </div>
          ))}
        </CardContent>
      </Card>

      {/* Daily attendance table */}
      <Card>
        <CardContent className="p-4 space-y-3">
          <div className="font-semibold text-slate-900">Attendance — {date}</div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="att-table">
              <thead className="bg-slate-50 text-slate-600 text-xs uppercase tracking-wide">
                <tr>
                  <th className="text-left px-3 py-2">Promoter</th>
                  <th className="text-left px-3 py-2">Dealer</th>
                  <th className="text-left px-3 py-2">Check-In</th>
                  <th className="text-left px-3 py-2">Check-Out</th>
                  <th className="text-left px-3 py-2">Location</th>
                  <th className="text-left px-3 py-2">Flags</th>
                  <th className="text-right px-3 py-2">Actions</th>
                </tr>
              </thead>
              <tbody>
                {loading && <tr><td colSpan={7} className="text-center text-slate-400 py-6">Loading…</td></tr>}
                {!loading && filteredRows.length === 0 && (
                  <tr><td colSpan={7} className="text-center text-slate-400 py-6" data-testid="att-empty">No attendance records match the filters.</td></tr>
                )}
                {!loading && filteredRows.map((r) => (
                  <tr key={r.id} className="border-t border-slate-100"
                      data-testid={`att-row-${r.promoter_id?.slice(0, 8)}`}>
                    <td className="px-3 py-2 font-medium">{r.promoter_name}</td>
                    <td className="px-3 py-2 text-slate-600">{r.check_in_dealer_name || "—"}</td>
                    <td className="px-3 py-2 text-slate-600">{fmtTime(r.check_in_at)}</td>
                    <td className="px-3 py-2 text-slate-600">{fmtTime(r.check_out_at)}</td>
                    <td className="px-3 py-2">
                      <LocationCell row={r} />
                    </td>
                    <td className="px-3 py-2 space-x-1">
                      {r.check_in_late && <Badge className="bg-rose-100 text-rose-800">Late</Badge>}
                      {r.check_out_early && <Badge className="bg-amber-100 text-amber-800">Early</Badge>}
                      {r.absent && <Badge className="bg-slate-200 text-slate-700">Absent</Badge>}
                      {r.manual_override && <Badge className="bg-indigo-100 text-indigo-800">Override</Badge>}
                    </td>
                    <td className="px-3 py-2 text-right">
                      <Button size="sm" variant="outline"
                              onClick={() => setOverrideRow({ ...r })}
                              data-testid={`att-override-${r.promoter_id?.slice(0, 8)}`}>
                        <Edit2 size={12} className="mr-1" /> Correct
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      <OverrideDialog row={overrideRow} onClose={() => setOverrideRow(null)} onSaved={load} date={date} />
    </div>
  );
}

function OverrideDialog({ row, onClose, onSaved, date }) {
  const [form, setForm] = useState({ check_in_at: "", check_out_at: "", mark_absent: false, reason: "" });
  useEffect(() => {
    if (row) {
      setForm({
        check_in_at: row.check_in_at || "",
        check_out_at: row.check_out_at || "",
        mark_absent: !!row.absent,
        reason: "",
      });
    }
  }, [row]);
  if (!row) return null;
  const save = async () => {
    if (!form.reason.trim()) { toast.error("Reason is required for override"); return; }
    try {
      await api.post("/promoter/attendance/override", {
        promoter_id: row.promoter_id,
        date,
        check_in_at: form.check_in_at || null,
        check_out_at: form.check_out_at || null,
        mark_absent: form.mark_absent,
        reason: form.reason,
      });
      toast.success("Attendance corrected");
      onSaved();
      onClose();
    } catch (e) {
      toast.error("Could not save", { description: formatApiErrorDetail(e.response?.data?.detail) });
    }
  };
  return (
    <Dialog open={!!row} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="att-override-dialog">
        <DialogHeader><DialogTitle>Correct attendance — {row.promoter_name}</DialogTitle></DialogHeader>
        <div className="space-y-3 text-sm">
          <div className="space-y-1">
            <Label className="text-[11px] uppercase font-semibold text-slate-600">Check-In (ISO datetime, leave blank to clear)</Label>
            <Input value={form.check_in_at} onChange={(e) => setForm((f) => ({ ...f, check_in_at: e.target.value }))}
                   placeholder="2026-06-25T10:00:00+00:00" data-testid="override-checkin" />
          </div>
          <div className="space-y-1">
            <Label className="text-[11px] uppercase font-semibold text-slate-600">Check-Out (ISO datetime)</Label>
            <Input value={form.check_out_at} onChange={(e) => setForm((f) => ({ ...f, check_out_at: e.target.value }))}
                   placeholder="2026-06-25T18:00:00+00:00" data-testid="override-checkout" />
          </div>
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={form.mark_absent}
                   onChange={(e) => setForm((f) => ({ ...f, mark_absent: e.target.checked }))}
                   data-testid="override-absent" />
            Mark as Absent
          </label>
          <div className="space-y-1">
            <Label className="text-[11px] uppercase font-semibold text-slate-600">Reason (required, audited)</Label>
            <Textarea value={form.reason} onChange={(e) => setForm((f) => ({ ...f, reason: e.target.value }))}
                      rows={2} data-testid="override-reason" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="override-cancel">Cancel</Button>
          <Button onClick={save} className="bg-slate-900 hover:bg-slate-800 text-white" data-testid="override-save">Save correction</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Stat({ label, value, testid, tone }) {
  const toneCls = {
    emerald: "text-emerald-700", amber: "text-amber-700",
    rose: "text-rose-700", slate: "text-slate-700",
  }[tone] || "text-slate-900";
  return (
    <Card data-testid={testid}>
      <CardContent className="p-3">
        <div className="text-[10px] uppercase tracking-wider text-slate-500">{label}</div>
        <div className={`font-heading text-2xl font-bold mt-1 ${toneCls}`}>{value}</div>
      </CardContent>
    </Card>
  );
}

function fmtTime(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }); }
  catch { return iso; }
}



function SummaryStat({ label, value, tone }) {
  const toneCls = {
    emerald: "bg-emerald-50 text-emerald-700",
    rose:    "bg-rose-50 text-rose-700",
    amber:   "bg-amber-50 text-amber-700",
    indigo:  "bg-indigo-50 text-indigo-700",
  }[tone] || "bg-slate-50 text-slate-700";
  return (
    <div className={`rounded p-2 ${toneCls}`}>
      <div className="text-[10px] uppercase font-semibold tracking-wider opacity-70">{label}</div>
      <div className="text-lg font-bold">{value ?? 0}</div>
    </div>
  );
}

/** Iter43 — location cell for PM/Admin verification.
 *  Shows check-in + check-out coords with Google Maps deep-links + accuracy.
 *  Falls back to "No location" / "Permission denied" hints.
 */
function LocationCell({ row }) {
  const ci = row.check_in_lat != null && row.check_in_lng != null
    ? { lat: row.check_in_lat, lng: row.check_in_lng, acc: row.check_in_accuracy }
    : null;
  const co = row.check_out_lat != null && row.check_out_lng != null
    ? { lat: row.check_out_lat, lng: row.check_out_lng, acc: row.check_out_accuracy }
    : null;
  const map = (lat, lng) => `https://www.google.com/maps/search/?api=1&query=${lat},${lng}`;
  if (!ci && !co) {
    return (
      <span className="text-[11px] text-slate-400 italic" data-testid={`att-loc-none-${row.promoter_id?.slice(0,8)}`}>
        <MapPin size={11} className="inline mr-1" /> Not captured
      </span>
    );
  }
  return (
    <div className="text-[11px] space-y-0.5" data-testid={`att-loc-${row.promoter_id?.slice(0,8)}`}>
      {ci && (
        <a href={map(ci.lat, ci.lng)} target="_blank" rel="noopener noreferrer"
           className="text-blue-700 hover:underline font-mono flex items-center gap-1"
           title={`Check-in — accuracy ${ci.acc ? Math.round(ci.acc) + 'm' : 'n/a'}`}
           data-testid={`att-loc-checkin-${row.promoter_id?.slice(0,8)}`}>
          <MapPin size={10} /> {ci.lat.toFixed(5)}, {ci.lng.toFixed(5)}
          {ci.acc && <span className="text-slate-500">±{Math.round(ci.acc)}m</span>}
          <ExternalLink size={9} />
        </a>
      )}
      {co && (
        <a href={map(co.lat, co.lng)} target="_blank" rel="noopener noreferrer"
           className="text-emerald-700 hover:underline font-mono flex items-center gap-1"
           title={`Check-out — accuracy ${co.acc ? Math.round(co.acc) + 'm' : 'n/a'}`}
           data-testid={`att-loc-checkout-${row.promoter_id?.slice(0,8)}`}>
          <MapPin size={10} /> {co.lat.toFixed(5)}, {co.lng.toFixed(5)}
          {co.acc && <span className="text-slate-500">±{Math.round(co.acc)}m</span>}
          <ExternalLink size={9} />
        </a>
      )}
    </div>
  );
}
